
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { useThemeColors } from '@/hooks/useThemeColors'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { ActionButtons } from '@/components/common/ActionButtons'

export default function NavigationsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const tableRef = useRef<DataTableRef>(null)
  const colors = useThemeColors()
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const {
    isOpen: isBulkConfirmOpen,
    onOpen: onBulkConfirmOpen,
    onClose: onBulkConfirmClose,
  } = useDisclosure()
  const {
    isOpen: isReloginModalOpen,
    onOpen: onReloginModalOpen,
    onClose: onReloginModalClose,
  } = useDisclosure()

  const [activeTab, setActiveTab] = useState<'all' | 'trash'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const hasShownReloginModalRef = useRef(false)

  const showReloginModalFromState =
    (location.state as { showReloginModal?: boolean } | null | undefined)?.showReloginModal ?? false

  useEffect(() => {
    if (showReloginModalFromState && !hasShownReloginModalRef.current) {
      hasShownReloginModalRef.current = true
      onReloginModalOpen()
    }
  }, [showReloginModalFromState, onReloginModalOpen])

  // Get access permissions for current route
  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        name: {
          name: 'Name',
          withSort: true,
        },
        slug: {
          name: 'Slug',
          withSort: true,
        },
        parent_navigation_name: {
          name: 'Parent Name',
          withSort: false,
        },
        active: {
          name: 'Status',
          withSort: true,
          badge: {
            'Active': 'green',
            'Inactive': 'orange',
          },
          badgeLabels: {
            'Active': 'Active',
            'Inactive': 'Inactive',
          },
        },
        show_in_menu: {
          name: 'Display Status',
          withSort: true,
          badge: {
            'Yes': 'green',
            'No': 'gray',
          },
          badgeLabels: {
            'Yes': 'Yes',
            'No': 'No',
          },
        },
        updated_at: {
          name: 'Updated At',
          withSort: true,
        },
      },
      dataSource: activeTab === 'trash' ? '/system-settings/archived/navigation' : '/system-settings/navigation',
      softDelete: true,
      primaryKey: 'id',
      displayInModal: false,
      noRecordText: 'No navigations found',
      otherActions: [],
    }),
    [activeTab]
  )

  // Permissions from access helper
  const permissions = useMemo(
    () => {
      const perms = {
        can_edit: access.can_edit,
        can_delete: access.can_delete,
        can_create: access.can_create,
        can_view: access.can_view,
        can_export: access.can_export,
        can_import: access.can_import,
      }
      return perms
    },
    [access.can_edit, access.can_delete, access.can_create, access.can_view, access.can_export, access.can_import]
  )

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these items from your site. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onBulkConfirmOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url = ''
      let payload: any = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/system-settings/navigation/bulk/restore'
        successMessage = 'Navigations restored successfully'
      } else if (bulkAction === 'delete') {
        url = activeTab === 'trash'
          ? '/system-settings/navigation/bulk/force-delete'
          : '/system-settings/navigation/bulk/delete'
        successMessage = activeTab === 'trash'
          ? 'Navigations permanently deleted successfully'
          : 'Navigations moved to trash successfully'
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onBulkConfirmClose()
      if (bulkAction === 'delete') {
        onReloginModalOpen()
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleConfirm = () => {
    handleConfirmBulkAction()
  }

  return (
    <AdminLayout>
        <Box
          p={4}
          h="100%"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
            {/* Header */}
            <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
              <CardHeader flexShrink={0} py={3}>
                <Heading size="md" mb={1}>Navigation</Heading>
                <Text fontSize="sm" color={colors.text}>
                  Customize and manage navigation menu structure and order
                </Text>
              </CardHeader>

              {/* Tabs and Action Buttons */}
              <CardHeader py={0} px={0} flexShrink={0}>
                <Box pt={2} pb={0}>
                  <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                    <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                      <TabList borderBottom="none">
                        <Tab>
                          All <Badge ml={2} colorScheme="blue">{totalRows}</Badge>
                        </Tab>
                        {totalTrash > 0 && (
                          <Tab>
                            Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                          </Tab>
                        )}
                      </TabList>
                    </Tabs>
                    <Box pb={2}>
                      <ActionButtons
                        pathname="/system-settings/navigation"
                        createLabel="Create New Navigation"
                      />
                    </Box>
                  </HStack>
                  <Box 
                    borderBottom="2px" 
                    borderColor={colors.border}
                    mx={4}
                  />
                </Box>
              </CardHeader>

              {/* Bulk Actions and Search */}
              <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
                <HStack spacing={4} flexWrap="wrap">
                  {access.can_delete && (
                    <HStack spacing={2} flex="0 1 auto" minW="250px">
                      <Select
                        placeholder="Bulk actions"
                        size="sm"
                        value={bulkAction}
                        onChange={(e) => {
                          e.stopPropagation()
                          setBulkAction(e.target.value)
                        }}
                      >
                        {activeTab === 'trash' && <option value="restore">Restore</option>}
                        {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                        {activeTab === 'all' && <option value="delete">Delete</option>}
                      </Select>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={handleBulkActionClick}
                      >
                        Apply
                      </Button>
                    </HStack>
                  )}

                  <Box flex="0 1 auto" minW="250px" maxW="400px">
                    <InputGroup size="sm">
                      <Input
                        ref={searchRef}
                        placeholder="Search navigations..."
                        onKeyDown={handleKeyDown}
                        defaultValue={searchQuery}
                        pr="2.5rem"
                      />
                      <InputRightElement width="2.5rem">
                        <IconButton
                          aria-label="Search"
                          icon={<Search size={16} />}
                          size="sm"
                          h="1.75rem"
                          w="1.75rem"
                          onClick={handleSearch}
                          variant="ghost"
                        />
                      </InputRightElement>
                    </InputGroup>
                  </Box>
                </HStack>
              </CardHeader>

              {/* Table */}
              <CardBody 
                p={0} 
                pt={1}
                pb={4}
                mx={5}
                flex="1" 
                overflow="hidden" 
                display="flex" 
                flexDirection="column" 
                minH={0}
                position="relative"
              >
                <Box h="100%" display="flex" flexDirection="column" minH={0}>
                  <DataTable
                    ref={tableRef}
                    options={tableOptions}
                    params={tableParams}
                    access={permissions}
                    setSubSub={showSubSubCallback}
                    onOpenModal={(id) => navigate(`/system-settings/navigation/${id}`)}
                    onDeleteSuccess={onReloginModalOpen}
                  />
                </Box>
              </CardBody>
            </Card>
          </VStack>
        </Box>

        {/* Confirmation Modal */}
        <Modal isOpen={isBulkConfirmOpen} onClose={onBulkConfirmClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Confirmation</ModalHeader>
            <ModalBody>
              <Text>{modalMessage}</Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onBulkConfirmClose} isDisabled={isBulkProcessing}>
                Cancel
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={handleConfirm}
                isLoading={isBulkProcessing}
                loadingText="Processing..."
              >
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

      {/* Relogin Required Modal */}
      <Modal
        isOpen={isReloginModalOpen}
        onClose={() => {
          onReloginModalClose()
          hasShownReloginModalRef.current = false
          navigate('/system-settings/navigation', { replace: true, state: {} })
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Notice</ModalHeader>
          <ModalBody>
            <Text>You need to relogin for the changes to take effect.</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={() => {
                onReloginModalClose()
                hasShownReloginModalRef.current = false
                navigate('/system-settings/navigation', { replace: true, state: {} })
              }}
            >
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      </AdminLayout>
  )
}

